import { useCallback, useState } from 'react';

import { useExperience } from '@cctv/contexts/ExperienceContext';
import { useExperienceState } from '@cctv/contexts/ExperienceStateContext';
import { CosmeticPlacement } from '@cctv/types';

export interface SaveAvatarParams {
  image?: string;
  cosmetics?: CosmeticPlacement[];
}

export function useSaveAvatar() {
  const { code, experienceFetch } = useExperience();
  const { participant, setParticipant } = useExperienceState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveAvatar = useCallback(
    async ({ image, cosmetics }: SaveAvatarParams) => {
      if (!code) {
        setError('Missing experience code');
        return { success: false, error: 'Missing experience code' } as const;
      }

      setIsLoading(true);
      setError(null);

      try {
        const res = await experienceFetch(`/api/experiences/${encodeURIComponent(code)}/avatar`, {
          method: 'POST',
          body: JSON.stringify({ avatar: { image, cosmetics } }),
        });
        const data = await res.json();
        if (!res.ok || data?.success === false) {
          const msg = data?.error || 'Failed to save avatar';
          setError(msg);
          return { success: false, error: msg } as const;
        }
        if (participant && data.avatar) {
          setParticipant({ ...participant, avatar: data.avatar });
        }
        return { success: true } as const;
      } catch (e: any) {
        const msg =
          e?.message === 'Authentication expired'
            ? 'Authentication expired'
            : 'Connection error. Please try again.';
        setError(msg);
        return { success: false, error: msg } as const;
      } finally {
        setIsLoading(false);
      }
    },
    [code, experienceFetch, participant, setParticipant],
  );

  return { saveAvatar, isLoading, error, setError };
}
